import {createStore} from 'vuex'
import util from '../common/util'
import logger from '../common/util/logger'
import brandMark from '@/assets/brand/dbg-markets-mark.png'
import {getDisplayCurrency} from '@/common/constants/displayCurrencies'
import {
    getCachedDisplayCurrency,
    getCachedDisplayCurrencyState,
    normalizeCachedDisplayCurrency,
    persistDisplayCurrency,
    requestDisplayCurrencyRate
} from '@/common/services/displayCurrencyService'

const initialDisplayCurrencyState = getCachedDisplayCurrencyState()
const initialDisplayCurrency = initialDisplayCurrencyState
    ? initialDisplayCurrencyState.currency
    : getCachedDisplayCurrency()
const initialDisplayCurrencyRate = initialDisplayCurrencyState
    ? initialDisplayCurrencyState.rate
    : '1'

const store = createStore({
    state: {
        userinfo: false, //用户身份信息
        accounts: false, //用户余额相关信息
        config: {
            site_name: 'DBG Markets',
            logo: brandMark
        }, //站点全局配置
        // 主题
        userTheme: util.getCache('userTheme') === 'light' ? 'light' : 'dark',
        userThemeIndex: util.getCache('userThemeIndex') ? util.getCache('userThemeIndex') : '1',
        displayCurrencyCode: initialDisplayCurrency.code,
        symbol: initialDisplayCurrency.symbol,
        symbol_rate: initialDisplayCurrencyRate
    },
    getters: {
        displayCurrency: state => getDisplayCurrency(state.displayCurrencyCode),
        symbol: state => state.symbol || getDisplayCurrency(state.displayCurrencyCode).symbol,
        symbol_rate: state => state.symbol_rate || '1',
        isLogin: state => {
            return state.userinfo !== false;
        },
        userAccount: state => {
            return state.userinfo.phone !== null && state.userinfo.phone !== undefined ? state.userinfo.phone : state.userinfo.email;
        },
        userNickname: state => {
            if (state.userinfo.nickname !== null && state.userinfo.nickname !== undefined && state.userinfo.nickname !== '') {
                return state.userinfo.nickname;
            }
            return state.userinfo.phone !== null && state.userinfo.phone !== undefined && state.userinfo.phone !== '' ? state.userinfo.phone : state
                .userinfo.email;
        },
        userId: state => {
            if (state.userinfo !== false && state.userinfo.id) {
                return state.userinfo.id;
            }
            return false;
        },
        userPhone: state => {
            return state.userinfo.phone !== null && state.userinfo.phone !== undefined ? state.userinfo.phone : false;
        },
        userEmail: state => {
            return state.userinfo.email !== null && state.userinfo.email !== undefined ? state.userinfo.email : false;
        },
        googleCode: state => {
            if (typeof state.userinfo.google2fa === 'undefined') {
                return false;
            }
            return state.userinfo.google2fa !== null && state.userinfo.google2fa !== undefined ? state.userinfo.google2fa : false;
        },
        balanceNull: state => {
            return {
                legal_balance: "0.00",
                balance: "0.00",
                lock_balance: "0.00",
                trade_balance: "0.00",
                lock_trade: "0.00",
                contract_balance: "0.00",
                lock_contract: "0.00",
                future_balance: "0.00",
                lock_future: "0.00",
                finance_balance: "0.00",
                lock_finance: "0.00"
            }
        },
        accounts: state => {
            return state.accounts || {
                legal_balance: "0.00",
                balance: "0.00",
                lock_balance: "0.00",
                trade_balance: "0.00",
                lock_trade: "0.00",
                contract_balance: "0.00",
                lock_contract: "0.00",
                future_balance: "0.00",
                lock_future: "0.00",
                finance_balance: "0.00",
                lock_finance: "0.00",
                comebit_balance: "0.00"
            }
        },
        // 主题
        userTheme: state => {
            return state.userTheme
        },
        userThemeIndex: state => {
            return state.userThemeIndex
        }
    },
    mutations: {
        userLogin(state, _userinfo) {
            if (_userinfo) {
                state.userinfo = _userinfo;
                if (_userinfo['token']) {
                    util.setCache('token', _userinfo['token']);
                }
                // 同时将 userinfo 存储到 localStorage，供路由守卫使用
                util.setCache('userinfo', JSON.stringify(_userinfo));
            } else {
                state.userinfo = false;
                util.delCache('token');
                util.delCache('userinfo');
            }
        },
        updateInfo(state, payload) {
            // payload 可以是 {field, value} 对象，或者直接是对象用于批量更新
            if (payload && typeof payload === 'object') {
                if (payload.field && payload.value !== undefined) {
                    // 单个字段更新: {field: 'nickname', value: '新昵称'}
                    if (state.userinfo && typeof state.userinfo === 'object') {
                        state.userinfo[payload.field] = payload.value;
                    }
                } else {
                    // 批量更新: {nickname: '新昵称', email: 'new@email.com'}
                    if (state.userinfo && typeof state.userinfo === 'object') {
                        Object.assign(state.userinfo, payload);
                    }
                }
            }
        },
        setAccounts(state, accounts) {
            state.accounts = accounts;
        },
        setConfig(state, data) {
            state.config = {
                ...(data || {}),
                site_name: 'DBG Markets',
                logo: brandMark
            };
        },
        updateTheme(state, data) {
            const theme = data === 'light' ? 'light' : 'dark';
            state.userTheme = theme;
            util.setCache('userTheme', theme);
        },
        applyDisplayCurrency(state, payload) {
            const currency = getDisplayCurrency(
                payload && payload.currency ? payload.currency : state.displayCurrencyCode
            );
            state.displayCurrencyCode = currency.code;
            state.symbol = currency.symbol;
            state.symbol_rate = payload && payload.rate ? String(payload.rate) : '1';
        },
        /**
         * 更新本地计价货币符号
         * @param state
         * @param value
         */
        updateSymbol(state,value){
            state.symbol = value;
        },
        /**
         * 更新本地计价货币汇率
         * @param state
         * @param value
         */
        updateSRate(state,value){
            state.symbol_rate = String(value);
        },
        /**
         * 更新本地计价货币
         * @param state
         * @param value
         */
        updateCurrency(state,value){
            const currency = getDisplayCurrency(value);
            state.displayCurrencyCode = currency.code;
            state.symbol = currency.symbol;
            util.setCache('local-currency', JSON.stringify({
                text: currency.code,
                value: currency.code,
                symbol: currency.symbol
            }));
        },
        updateLanguage(state, lang) {
            // 更新语言设置
            state.currentLanguage = lang;
            util.setCache('lang', lang);
        }
    },
    actions: {
        async switchDisplayCurrency({commit}, currencyCode) {
            const currency = getDisplayCurrency(currencyCode);
            const rate = await requestDisplayCurrencyRate(currency.code);
            const result = persistDisplayCurrency(currency.code, rate);
            commit('applyDisplayCurrency', result);
            return result;
        },
        async initializeDisplayCurrency({commit, dispatch}) {
            const cached = normalizeCachedDisplayCurrency();
            if (cached) {
                commit('applyDisplayCurrency', cached);
                return cached;
            }

            const preferred = getCachedDisplayCurrency();
            try {
                return await dispatch('switchDisplayCurrency', preferred.code);
            } catch (error) {
                const fallback = persistDisplayCurrency('USD', '1');
                commit('applyDisplayCurrency', fallback);
                logger.warn('⚠️ 计价汇率初始化失败，暂用 USD 基准汇率:', error);
                return fallback;
            }
        }
    }
});

export default store
